import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Groupement {
    id: number;
    name: string;
    isGlobal: boolean;
    logoUrl?: string | null;
    displayLogoOnPublications?: boolean;
}
interface UserState {
    isAuthenticated: boolean;
    isAdmin: boolean;
    id: number | null;
    email: string | null;
    agencyName: string | null;
    token: string | null;
    quota: number | null;
    role: string | null;
    isSsoUser: boolean;
    userMyId: string | null;
    groupement: Groupement | null;
    autoPublish: boolean;
    logoUrl: string | null;
    displayLogoOnPublications: boolean;
}

const initialState: UserState = {
    isAuthenticated: !!localStorage.getItem('userToken'),
    isAdmin: localStorage.getItem('isAdmin') === 'true',
    id: localStorage.getItem('userId') ? parseInt(localStorage.getItem('userId')!, 10) : null,
    email: localStorage.getItem('userEmail'),
    agencyName: localStorage.getItem('agencyName'),
    token: localStorage.getItem('userToken'),
    quota: localStorage.getItem('quota') ? parseInt(localStorage.getItem('quota')!, 10) : null,
    role: localStorage.getItem('roles'),
    isSsoUser: localStorage.getItem('ssoUser') === 'true',
    userMyId: localStorage.getItem('userMyId'),
    groupement: localStorage.getItem('groupement')
        ? JSON.parse(localStorage.getItem('groupement')!)
        : null,
    autoPublish: localStorage.getItem('autoPublish') === 'true',
    logoUrl: localStorage.getItem('logoUrl'),
    displayLogoOnPublications: localStorage.getItem('displayLogoOnPublications') === 'true',
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUser: (
            state,
            action: PayloadAction<{
                isAuthenticated: boolean;
                isAdmin: boolean;
                id: number;
                email: string;
                agencyName: string;
                token: string;
                quota: number;
                role: string | null;
                isSsoUser: boolean;
                userMyId: string | null;
                groupement: Groupement | null;
                autoPublish: boolean;
                logoUrl?: string | null;
                displayLogoOnPublications?: boolean;
            }>,
        ) => {
            state.isAuthenticated = true;
            state.isAdmin = action.payload.isAdmin;
            state.id = action.payload.id;
            state.email = action.payload.email;
            state.agencyName = action.payload.agencyName;
            state.token = action.payload.token;
            state.quota = action.payload.quota;
            state.role = action.payload.role;
            state.isSsoUser = action.payload.isSsoUser;
            state.userMyId = action.payload.userMyId;
            state.groupement = action.payload.groupement;
            state.autoPublish = action.payload.autoPublish;
            state.logoUrl = action.payload.logoUrl || null;
            state.displayLogoOnPublications = action.payload.displayLogoOnPublications || false;

            localStorage.setItem('userToken', action.payload.token);
            localStorage.setItem('userEmail', action.payload.email);
            localStorage.setItem('agencyName', action.payload.agencyName);
            localStorage.setItem('roles', action.payload.role || '');
            localStorage.setItem('userId', action.payload.id.toString());
            localStorage.setItem('isAdmin', action.payload.isAdmin.toString());
            localStorage.setItem('quota', action.payload.quota.toString());
            localStorage.setItem('isSsoUser', action.payload.isSsoUser.toString());
            localStorage.setItem('userMyId', action.payload.userMyId || '');
            localStorage.setItem('groupement', JSON.stringify(action.payload.groupement || ''));
            localStorage.setItem('autoPublish', action.payload.autoPublish.toString());
            localStorage.setItem('logoUrl', action.payload.logoUrl || '');
            localStorage.setItem(
                'displayLogoOnPublications',
                (action.payload.displayLogoOnPublications || false).toString(),
            );
        },
        logout: state => {
            state.isAuthenticated = false;
            state.isAdmin = false;
            state.id = null;
            state.email = null;
            state.agencyName = null;
            state.token = null;
            state.quota = null;
            state.role = null;
            state.isSsoUser = false;
            state.userMyId = null;
            state.groupement = null;
            state.autoPublish = false;
            state.logoUrl = null;
            state.displayLogoOnPublications = false;

            localStorage.removeItem('userToken');
            localStorage.removeItem('userEmail');
            localStorage.removeItem('agencyName');
            localStorage.removeItem('roles');
            localStorage.removeItem('userId');
            localStorage.removeItem('isAdmin');
            localStorage.removeItem('quota');
            localStorage.removeItem('isSsoUser');
            localStorage.removeItem('userMyId');
            localStorage.removeItem('groupement');
            localStorage.removeItem('autoPublish');
            localStorage.removeItem('logoUrl');
            localStorage.removeItem('displayLogoOnPublications');
        },
        initializeUser: state => {
            const token = localStorage.getItem('userToken');
            if (token) {
                state.isAuthenticated = true;
                state.token = token;
                state.email = localStorage.getItem('userEmail');
                state.agencyName = localStorage.getItem('agencyName');
                state.isAdmin = localStorage.getItem('isAdmin') === 'true';
                state.id = localStorage.getItem('userId')
                    ? parseInt(localStorage.getItem('userId')!, 10)
                    : null;
                state.quota = localStorage.getItem('quota')
                    ? parseInt(localStorage.getItem('quota')!, 10)
                    : null;
                state.role = localStorage.getItem('roles');
                state.isSsoUser = localStorage.getItem('isSsoUser') === 'true';
                state.userMyId = localStorage.getItem('userMyId');
                state.groupement = localStorage.getItem('groupement')
                    ? JSON.parse(localStorage.getItem('groupement')!)
                    : null;
                state.autoPublish = localStorage.getItem('autoPublish') === 'true';
                state.logoUrl = localStorage.getItem('logoUrl');
                state.displayLogoOnPublications =
                    localStorage.getItem('displayLogoOnPublications') === 'true';
            }
        },
        setAutoPubNf: (state, action: PayloadAction<boolean>) => {
            state.autoPublish = action.payload;
            localStorage.setItem('autoPublish', action.payload.toString());
        },
        setLogoDisplay: (state, action: PayloadAction<boolean>) => {
            state.displayLogoOnPublications = action.payload;
            localStorage.setItem('displayLogoOnPublications', action.payload.toString());
        },
        setLogoUrl: (state, action: PayloadAction<string | null>) => {
            state.logoUrl = action.payload;
            localStorage.setItem('logoUrl', action.payload || '');
        },
        setGroupement: (state, action: PayloadAction<Groupement | null>) => {
            state.groupement = action.payload;
            localStorage.setItem('groupement', JSON.stringify(action.payload || ''));
        },
    },
});

export const {
    setUser,
    logout,
    initializeUser,
    setAutoPubNf,
    setLogoDisplay,
    setLogoUrl,
    setGroupement,
} = userSlice.actions;
export default userSlice.reducer;
